import type { StudioChatModel, StudioModelPricing } from "../components/studio/studioChatModels";
import type { VideoRates } from "../components/studio/studioVideoPricing";
import { getAuthHeaders } from "./network_calls";
import { usageApiUrl } from "./usage";
import defaultPricing from "./defaultModelPricing.json";

export type ModelPricingCatalog = {
  imagePricing?: Record<string, number>;
  audioPricing?: Record<string, Record<string, number>>;
  tokenCosts?: import("../components/createShared").TokenCostsLike;
  rankingCriteria?: ModelPriceSort[];
  rankingNotes?: string;
  models: Pick<StudioChatModel, "id" | "label" | "provider" | "pricing" | "contextLength" | "ranks">[];
  videoPricing?: VideoRates;
  tokensPerUsd?: number;
  conversionStatus: string;
  productionBilling: boolean;
  updatedAt: string | null;
  catalogStatus: string;
  defaultModelId?: string;
  rankings?: Partial<Record<ModelPriceSort, string[]>>;
};
export type ModelPriceSort = "overall" | "smartest" | "creativity" | "fastest" | "cheapest";
/** Display-only snapshot from OpenRouter and Fable's configured rates. Billing
 * always uses the server's quote and measured provider usage. */
export const DEFAULT_MODEL_PRICING: ModelPricingCatalog = defaultPricing;
let lastKnownCatalog: ModelPricingCatalog | undefined;

/** Fill omitted media prices per field so older responses cannot erase them. */
export function resolveModelPricing(catalog: ModelPricingCatalog, fallback = DEFAULT_MODEL_PRICING): ModelPricingCatalog {
  const rates = (defaults: Record<string, number> = {}, current: Record<string, number> = {}) => ({
    ...defaults,
    ...Object.fromEntries(Object.entries(current).filter(([, value]) => typeof value === "number" && Number.isFinite(value) && value >= 0)),
  });
  const audioDefaults = fallback.audioPricing ?? {};
  const videoDefaults = fallback.videoPricing ?? {};
  return {
    ...catalog,
    imagePricing: rates(fallback.imagePricing, catalog.imagePricing),
    audioPricing: Object.fromEntries([...new Set([...Object.keys(audioDefaults), ...Object.keys(catalog.audioPricing ?? {})])]
      .map(length => [length, rates(audioDefaults[length], catalog.audioPricing?.[length])])),
    videoPricing: Object.fromEntries([...new Set([...Object.keys(videoDefaults), ...Object.keys(catalog.videoPricing ?? {})])]
      .map(mode => [mode, rates(videoDefaults[mode], catalog.videoPricing?.[mode]) as VideoRates[string]])),
  };
}

export async function loadModelPricing(signal?: AbortSignal): Promise<ModelPricingCatalog> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, 15000);
  try {
    const headers = await getAuthHeaders();
    if (!headers.Authorization) throw new Error("Sign in to view pricing.");
    const response = await fetch(usageApiUrl("/usage/pricing"), { headers, signal: controller.signal });
    if (!response.ok) throw new Error("Could not load model pricing.");
    const catalog = await response.json();
    if (!Array.isArray(catalog.models) || !catalog.models.length ||
      !Number.isFinite(catalog.tokensPerUsd) || catalog.tokensPerUsd <= 0 ||
      catalog.models.some((model: ModelPricingCatalog["models"][number]) =>
        !model.id || !model.label || !modelPriceRange(model.pricing, "prompt", 1) || !modelPriceRange(model.pricing, "completion", 1)))
      throw new Error("Model pricing is not available yet.");
    const rankings = Object.fromEntries((["overall", "smartest", "creativity", "fastest", "cheapest"] as const).map(key => {
      const order = catalog.rankings?.[key];
      return [key, Array.isArray(order) && order.every(id => typeof id === "string")
        ? order : DEFAULT_MODEL_PRICING.rankings?.[key] || []];
    }));
    const resolved = resolveModelPricing({ ...catalog, rankings }, lastKnownCatalog || DEFAULT_MODEL_PRICING);
    lastKnownCatalog = resolved;
    return resolved;
  } catch (error) {
    if (signal?.aborted) throw error;
    return lastKnownCatalog || DEFAULT_MODEL_PRICING;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

/** Catalog prices are USD per model token. Include published context/time tiers. */
export function modelPriceRange(pricing: StudioModelPricing | undefined, key: "prompt" | "completion" | "input_cache_read", multiplier: number): [number, number] | null {
  if (!pricing || !Number.isFinite(multiplier) || multiplier <= 0) return null;
  const parse = (value: unknown) => typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : null;
  const base = parse(pricing[key]);
  if (base === null) return null;
  const rates = [base, ...(pricing.overrides || []).map(tier => parse(tier[key]) ?? base)];
  return [Math.min(...rates) * multiplier, Math.max(...rates) * multiplier];
}

export const MODEL_PRICE_TOKENS = 1_000;
export const MODEL_PRICE_WORDS = 500;

/** Standard uncached text rate, not the maximum long-context tier. Word counts
 * are only a display estimate; the backend settles measured provider cost. */
export function modelWordPrice(pricing: StudioModelPricing | undefined, key: "prompt" | "completion", tokensPerUsd: number | null, words = MODEL_PRICE_WORDS): number | null {
  if (tokensPerUsd === null || !Number.isFinite(tokensPerUsd) || tokensPerUsd <= 0 || !Number.isFinite(words) || words <= 0) return null;
  const rate = pricing?.[key];
  if (typeof rate !== "string" || rate.trim() === "" || !Number.isFinite(Number(rate)) || Number(rate) < 0) return null;
  return Number(rate) * words / 0.75 * tokensPerUsd;
}

export function sortPricingModels(catalog: ModelPricingCatalog, sort: ModelPriceSort) {
  // Current servers supply every criterion. Never substitute a local rank for
  // an explicitly empty server ranking. Older servers use the fallback below.
  const configured = catalog.rankings?.[sort] ?? (sort === "creativity" ? DEFAULT_MODEL_PRICING.rankings?.creativity : undefined);
  if (configured) {
    const positions = new Map(configured.map((id, index) => [id, index]));
    return [...catalog.models].sort((a, b) => (positions.get(a.id) ?? Infinity) - (positions.get(b.id) ?? Infinity) || a.label.localeCompare(b.label));
  }
  const ids = new Set(catalog.models.map(model => model.id));
  const rank = (order: string[] = []) => {
    const available = [...new Set(order.filter(id => ids.has(id)))];
    return new Map(available.map((id, index) => [id, index / Math.max(1, available.length - 1)]));
  };
  const smartest = rank(catalog.rankings?.smartest);
  const fastest = rank(catalog.rankings?.fastest);
  const prices = new Map(catalog.models.map(model => {
    const input = modelWordPrice(model.pricing, "prompt", catalog.tokensPerUsd ?? null);
    const output = modelWordPrice(model.pricing, "completion", catalog.tokensPerUsd ?? null);
    return [model.id, input !== null && output !== null ? input + output : Infinity];
  }));
  const sortedPrices = [...prices.values()].filter(Number.isFinite).sort((a, b) => a - b);
  const score = (model: ModelPricingCatalog["models"][number]) => {
    const price = prices.get(model.id) ?? Infinity;
    if (sort === "cheapest") return price;
    if (sort === "smartest") return smartest.get(model.id) ?? Infinity;
    if (sort === "fastest") return fastest.get(model.id) ?? Infinity;
    if (!smartest.has(model.id) || !fastest.has(model.id) || !Number.isFinite(price)) return Infinity;
    // Fable's recommendation, not an external benchmark: 60% intelligence,
    // 20% recent output speed, 20% combined input/output price. Use relative
    // ranks so units cannot dominate; equal prices get the same cost rank.
    const costRank = sortedPrices.indexOf(price) / Math.max(1, sortedPrices.length - 1);
    return 0.6 * smartest.get(model.id)! + 0.2 * fastest.get(model.id)! + 0.2 * costRank;
  };
  return [...catalog.models].sort((a, b) => score(a) - score(b) || a.label.localeCompare(b.label));
}

/** Display-only English word estimate. Settlement still uses actual provider
 * token counts, including billed reasoning, never this word conversion. */
export function modelWordPriceRange(pricing: StudioModelPricing | undefined, key: "prompt" | "completion", tokensPerUsd: number | null, words = MODEL_PRICE_WORDS): [number, number] | null {
  if (tokensPerUsd === null || !Number.isFinite(tokensPerUsd) || tokensPerUsd <= 0 || !Number.isFinite(words) || words <= 0) return null;
  return modelPriceRange(pricing, key, words / 0.75 * tokensPerUsd);
}

/** The catalog and settlement use the same conversion. Model tokens are not
 * characters or Fable wallet tokens; fractional Fable prices stay intact. */
export function modelTokenPriceRange(pricing: StudioModelPricing | undefined, key: "prompt" | "completion" | "input_cache_read", tokensPerUsd: number | null, modelTokens = MODEL_PRICE_TOKENS): [number, number] | null {
  if (tokensPerUsd === null || !Number.isFinite(tokensPerUsd) || tokensPerUsd <= 0 || !Number.isSafeInteger(modelTokens) || modelTokens < 0) return null;
  if (modelTokens === 0) return [0, 0];
  return modelPriceRange(pricing, key, modelTokens * tokensPerUsd);
}

export function modelUsagePriceEstimate(pricing: StudioModelPricing | undefined, tokensPerUsd: number | null, inputTokens: number, outputTokens: number): [number, number] | null {
  const input = modelTokenPriceRange(pricing, "prompt", tokensPerUsd, inputTokens);
  const output = modelTokenPriceRange(pricing, "completion", tokensPerUsd, outputTokens);
  return input && output ? [input[0] + output[0], input[1] + output[1]] : null;
}
